import { z } from "zod";

import { AgentColourNameSchema } from "../domain/chat/agent-colour.schema";
import { providerMetadataSchema } from "../domain/chat/provider-metadata.schema";
import { AgentDispatchResultSchema } from "./agent-dispatch-result";
import { WebFetchResultSchema } from "./web-fetch-result";

/**
 * Always the first SSE event of a fresh generation. Carries the opaque
 * `generationId` — the capability token that lets the client target THIS
 * specific generation when it later calls the stop or detach endpoints.
 *
 * Without it, those endpoints have no way to distinguish "stop the run I just
 * started" from "stop whatever happens to be running now", and a stale stop
 * request from the prior generation can tear down a freshly started one.
 *
 * @see apps/backend/src/_common/streaming/execution-slot/domain/ports/acquire-execution-slot.port.ts
 */
export const GenerationStartEventSchema = z.object({
  type: z.literal("generation-start"),
  generationId: z.string().uuid()
});

/**
 * Emitted by a provider adapter the moment the underlying SDK announces a
 * session — BEFORE any generation. Provider-agnostic: any SDK that exposes a
 * session-init handshake (Claude Agent SDK `system_init`, future Anthropic SDK,
 * etc.) translates that handshake into this single wire event.
 *
 * Daytona is the only emitter today: the IPC parser yields `stream-start` as
 * soon as it observes `system_init.session_id`. The captured id is durable
 * through abort because the on-disk JSONL at
 * `~/.claude/projects/<encoded-cwd>/<session-id>.jsonl` survives `process.exit`.
 *
 * Consumers (orchestrator, tracing middleware) persist `providerSessionId`
 * once here — replacing the prior pattern of accumulating it across
 * `message-end` variants and writing in a `finally`-block guard.
 *
 * Provider-specific infrastructural identifiers (Daytona `sandboxId`, future
 * provider-side handles) DO NOT travel on this event — they ride the typed
 * `onSandboxIdResolved` / equivalent callbacks on `StreamProviderInput`.
 * Keeping this event field-pure prevents the bag-of-anything regression that
 * motivated the migration off `onProviderMetadata`.
 */
export const StreamStartEventSchema = z.object({
  type: z.literal("stream-start"),
  /**
   * Provider-assigned session identifier captured at SDK session-init.
   * Daytona: Claude Agent SDK `session_id` from `system_init`.
   * Non-empty when present — the parser is responsible for not yielding an
   * empty-string id (mirrors the upstream Daytona IPC v2 schema constraint).
   */
  providerSessionId: z.string().min(1).optional()
});

/**
 * Opens a new text block. Fired once per text block by all provider adapters;
 * the frontend uses `blockId` to create a renderable text span in the accumulator.
 *
 * @see apps/backend/src/_common/streaming/providers/anthropic-stream-provider.adapter.ts
 * @see apps/backend/src/_common/streaming/providers/openai-stream-provider.adapter.ts
 */
export const TextStartEventSchema = z.object({
  type: z.literal("text-start"),
  blockId: z.string(),
  /** Non-null when this block was forwarded from a sub-agent (requires forwardSubagentText). */
  parentToolUseId: z.string().nullable().optional()
});

/**
 * Appends `delta` text to the open block identified by `blockId`. Emitted per
 * token; the frontend pipes each delta into the accumulator to build the
 * streaming text span.
 */
export const TextDeltaEventSchema = z.object({
  type: z.literal("text-delta"),
  blockId: z.string(),
  delta: z.string()
});

/**
 * Closes the text block identified by `blockId`. Signals the frontend to
 * finalize that text span (stop streaming indicator, commit to accumulator).
 */
export const TextEndEventSchema = z.object({
  type: z.literal("text-end"),
  blockId: z.string(),
  /** Transparent-capture carrier populated by later runner slices (NEX-2544/2545). Optional so today's adapters that don't emit it still parse. */
  providerMetadata: providerMetadataSchema.optional()
});

/**
 * Opens a reasoning/extended-thinking block. Emitted when the provider sends
 * its first reasoning token. Both the Anthropic and OpenAI adapters emit this.
 */
export const ThinkingStartEventSchema = z.object({
  type: z.literal("thinking-start"),
  blockId: z.string(),
  /** Non-null when this block was forwarded from a sub-agent (requires forwardSubagentText). */
  parentToolUseId: z.string().nullable().optional()
});

/**
 * Appends a reasoning token delta to the open thinking block identified by
 * `blockId`. Mirrors `text-delta` in structure but targets the thinking
 * accumulator lane.
 */
export const ThinkingDeltaEventSchema = z.object({
  type: z.literal("thinking-delta"),
  blockId: z.string(),
  delta: z.string()
});

/**
 * Closes the thinking block identified by `blockId`. `durationMs` carries the
 * total reasoning duration for UI "Thought for Xs" display.
 */
export const ThinkingEndEventSchema = z.object({
  type: z.literal("thinking-end"),
  blockId: z.string(),
  durationMs: z.number().optional(),
  /** Transparent-capture carrier populated by later runner slices (NEX-2544/2545). Optional so today's adapters that don't emit it still parse. */
  providerMetadata: providerMetadataSchema.optional()
});

/**
 * Announces a tool call has begun — `toolName` and `toolCallId` are known,
 * arguments are still streaming. Emitted by the Daytona IPC parser AND by the
 * two provider adapters (Anthropic, OpenAI). The frontend renders a pending
 * tool-call chip and fires `onToolCall`.
 *
 * @see apps/backend/src/external/daytona/daytona-ipc-parser.ts
 * @see apps/backend/src/external/daytona/ipc/infrastructure/translators/tool-use-block-start.translator.ts
 * @see apps/backend/src/external/daytona/ipc/infrastructure/translators/assistant-tool-call.translator.ts
 * @see apps/backend/src/_common/streaming/providers/anthropic-stream-provider.adapter.ts
 * @see apps/backend/src/_common/streaming/providers/openai-stream-provider.adapter.ts
 */
export const ToolInputStartEventSchema = z.object({
  type: z.literal("tool-input-start"),
  blockId: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
  parentToolUseId: z.string().nullable().optional(),
  // Present only on the Manual-mode resume re-emission of an approved call:
  // the model re-issued the tool with a fresh id and this links it back to the
  // original deferred toolCallId (see `reissueOf` on tool-input-available).
  reissueOf: z.string().optional()
});

/**
 * A raw chunk of a tool call's argument JSON as it streams in. COSMETIC /
 * display-only — NEVER JSON-parsed. The authoritative, fully-parsed arguments
 * arrive whole on `tool-input-available`; this event exists only so a future
 * live-typing render can show the argument JSON accumulating in real time.
 *
 * Emitted by the Anthropic adapter (`content_block_delta` with
 * `delta.type === "input_json_delta"`) and the OpenAI adapter
 * (`response.function_call_arguments.delta`). The Daytona Cue path also
 * emits this event, translated host-side from the agent runner's
 * `ToolUseBlockDelta` IPC events.
 *
 * @see apps/backend/src/_common/streaming/providers/anthropic-stream-provider.adapter.ts
 * @see apps/backend/src/_common/streaming/providers/openai-stream-provider.adapter.ts
 * @see apps/backend/src/external/daytona/ipc/infrastructure/translators/tool-use-block-delta.translator.ts
 */
export const ToolInputDeltaEventSchema = z.object({
  type: z.literal("tool-input-delta"),
  blockId: z.string(),
  toolCallId: z.string(),
  delta: z.string()
});

/**
 * Tool call arguments fully parsed. Carries the complete `input` object.
 * Emitted by all provider adapters once argument streaming completes.
 * The frontend resolves the pending tool-call chip to "ready" and fires
 * `onToolCallReady` to dispatch the actual tool execution.
 *
 * @see apps/backend/src/_common/streaming/providers/anthropic-stream-provider.adapter.ts
 * @see apps/backend/src/_common/streaming/providers/openai-stream-provider.adapter.ts
 */
export const ToolInputAvailableEventSchema = z.object({
  type: z.literal("tool-input-available"),
  blockId: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.record(z.string(), z.unknown()),
  parentToolUseId: z.string().nullable().optional(),
  // Backend-authoritative approval signal (single cross-adapter flag, AI-SDK
  // `needsApproval` semantics). When present, the frontend gates the tool on this
  // value instead of its local tools registry. Optional so consumers that don't
  // emit it fall back to the registry. A backend-gated tool executes server-side
  // after the user's verdict, so the approval settles as a verdict on the
  // continuation's `toolVerdicts` channel, never a synthesized `toolResults` entry.
  needsApproval: z.boolean().optional(),
  // Present only on the Manual-mode resume re-emission of an approved call: the
  // model re-issues the tool with a FRESH toolCallId, and `reissueOf` carries the
  // ORIGINAL deferred toolCallId the user approved. Consumers reconcile the
  // re-issued execution onto the original approval card (the FE drops the
  // transient fresh-id part; the backend re-keys the tool-output-available and
  // upgrades the original closure TOOL row with the real output).
  reissueOf: z.string().optional(),
  // Mirror of the Agent SDK tool input's `run_in_background` (Task + Bash both carry
  // it, so this is tool-agnostic, not an agent-only signal). True when the model
  // dispatched this tool to run in the background. Daytona/Agent SDK only.
  runInBackground: z.boolean().optional(),
  // The colour the dispatched agent's type declares, forwarded from the runner's
  // `assistant_tool_call`. Sourced from the agent definition in the sandbox, never
  // from `input` — the model does not write it. Absent for every non-Agent tool
  // call and for an agent type declaring no colour.
  agentColour: AgentColourNameSchema.optional(),
  // On a hold card (`needsApproval`), whose hold it is — `lead` when the main
  // thread is parked on it, `agent` when a subagent is — forwarded from the
  // runner's `assistant_tool_call`. Absent from a runner that predates it, and
  // on every call that opens no hold.
  heldFor: z.enum(["lead", "agent"]).optional(),
  /** Transparent-capture carrier populated by later runner slices (NEX-2544/2545). Optional so today's adapters that don't emit it still parse. */
  providerMetadata: providerMetadataSchema.optional()
});

/**
 * Settles a tool call. A tool call is EXECUTED or REJECTED; `reason` explains why.
 *
 * - `status` — the decision: `accepted` (the tool ran) or `rejected` (the user
 *   declined or the generation was cancelled). A `tool-output-available` is always
 *   a settled outcome; the frontend derives the transient "executing" phase locally
 *   from `input-available` state + pending-approval set removal (`toolCallPhase`).
 * - `output` — present when accepted: the tool's stringified result.
 * - `isError` — accepted-but-failed: applying the result threw. Drives error styling.
 * - `reason` — present when rejected: the single human-readable string the model
 *   reads on the next turn AND the user sees in the UI.
 *
 * `status`, `isError`, and `reason` are independent facets — no nested union.
 *
 * @see apps/frontend/src/common/chat/conversation-stream/conversation-stream-machine.ts
 */
/**
 * `isReplay` marks a result the SDK replayed rather than one a tool just
 * produced. A replayed result still settles the call it belongs to — settling is
 * idempotent on `toolCallId`, so a reader that ignores the field lands the same
 * terminal state twice rather than rendering twice, and a reader that reads it
 * can present the settle as recovered rather than fresh. Absent means "not known
 * to be a replay", never "known to be live": a sandbox baked before the runner
 * stamped it emits without the field.
 */
export const ToolOutputAvailableEventSchema = z.object({
  type: z.literal("tool-output-available"),
  toolCallId: z.string(),
  status: z.enum(["accepted", "rejected"]),
  output: z.string().optional(),
  isError: z.boolean().optional(),
  reason: z.string().optional(),
  isReplay: z.boolean().optional(),
  /**
   * The Agent dispatch's structured result, present only when this settles an
   * Agent call — the subagent's report and the run's totals, so a reader acts on
   * fields instead of parsing an answer back out of the prose the model was
   * shown. Shape and rationale: `AgentDispatchResultSchema`.
   *
   * Nothing renders it, which is its resting state rather than an unfinished
   * slice — it travels so a surface that wants a subagent's duration, its token
   * cost, or its report on its own terms has the fact on the wire instead of
   * needing a new one.
   *
   * It survives a reload: `ToolResult` carries it, and every fold projecting this
   * event into a persisted tool result copies it — on the host the turn
   * accumulator, the stream orchestrator's three write paths, the two row writers
   * and the cancellation composite, plus the background-agent producer's own pair;
   * in the browser the two live folds and the two read-back parsers. A fold added
   * beside any of them owes the same copy, or the fact lands on one path and not
   * its sibling.
   */
  agentResult: AgentDispatchResultSchema.optional(),
  /**
   * What a WebFetch measured about its own request — the HTTP response code and
   * its text, the bytes returned, and how long the fetch took. Present only
   * when this settles a WebFetch call.
   *
   * It is what separates a fetch that failed from a tool call that failed —
   * `WebFetchResultSchema` carries why the prose cannot answer that.
   *
   * Nothing renders it yet — NEX-4838's fetch renderer draws the answer and no
   * status chip — which is its resting state rather than an unfinished slice.
   * It travels so the chip has the fact to draw from.
   *
   * It reaches a live reader and no further; the hop-1 declaration on
   * `UserToolResultSchema` carries the reasoning and what carrying it further
   * would cost. Shape and reachability evidence: `WebFetchResultSchema`.
   */
  webFetchResult: WebFetchResultSchema.optional()
});

/**
 * A single unmodeled provider content block, forwarded verbatim. Emitted once
 * per block (no start/delta/end trio) for any block type without a dedicated
 * ChatStreamEvent — `server_tool_use`, `mcp_tool_use`, every `*_tool_result`
 * block, `container_upload`, `compaction`, and any FUTURE/unrecognized provider
 * block type. `redacted_thinking` left this set when it earned
 * `RedactedThinkingStreamEventSchema` below. `providerMetadata` is REQUIRED —
 * it is the event's content payload (`parentToolUseId` is attribution, not
 * content; mirrors the `unknown` ordered part).
 */
export const UnknownBlockStreamEventSchema = z.object({
  type: z.literal("unknown-block"),
  /** Non-null when this block was emitted inside a sub-agent's turn (requires forwardSubagentText). */
  parentToolUseId: z.string().nullable().optional(),
  providerMetadata: providerMetadataSchema
});

/**
 * The model reasoned at this position and the provider withheld the reasoning.
 * One event per block, at the block's close — a `redacted_thinking` block arrives
 * whole and streams no delta, so there is no start/end trio to pair it with.
 *
 * A content block, not a signal: it belongs to the turn that emitted it and takes
 * its slot among that turn's other parts, so stream order draws it where the gap
 * was. `parentToolUseId` is set when a sub-agent produced it.
 *
 * `providerMetadata` is REQUIRED and is the event's content: it holds the verbatim
 * provider block, whose opaque `data` has to reach the next request unchanged. The
 * block carries no readable text, so what a reader is being told is that reasoning
 * happened here and was redacted — never what it said.
 *
 * Nothing draws a bespoke row for it yet, which is a resting state rather than a
 * gap: it renders through the same passthrough row an
 * unmodelled block does, by an explicit arm rather than by falling into that
 * bucket unnamed. Typing it is what lets a later rendering decision branch on it.
 */
export const RedactedThinkingStreamEventSchema = z.object({
  type: z.literal("redacted-thinking"),
  /** Non-null when a sub-agent produced this block. */
  parentToolUseId: z.string().nullable().optional(),
  providerMetadata: providerMetadataSchema
});
